import {
  Order,
  OrderDto,
  DeleteResult,
  OrderLoadOptions,
  OrderUpdateOptions,
  OrderRemoveOptions,
  OrderSaveOptions,
  PaginationResult,
  PrintBlockItem,
  OrderStatuses,
  SharedLink,
  getMainFileOrNothing,
  getMainFileOrError,
  OrderInfo,
  OrderTypes,
  TalcColor,
} from 'src/app/shared';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  EMPTY,
  catchError,
  firstValueFrom,
  Observable,
  Subject,
  timeout,
} from 'rxjs';
import {
  ConfigService,
  HTTP_REQUESTS_TIMEOUT,
  PrintColorsConstant,
  StockItem,
  UserService,
} from 'src/app/core';
import { OrderPriceController } from './order-price-controller';
import { OrdersStatusEvent } from 'src/app/shared/interfaces/shared/services/order-service.in';

const _ = require('lodash');

@Injectable({ providedIn: 'root' })
export class OrderService {
  private priceUpdate: Subject<number> = new Subject();
  private _orderStatusEventSubject = new Subject<OrdersStatusEvent>();

  public get priceUpdate$() {
    return this.priceUpdate.pipe(catchError(() => EMPTY));
  }

  public get status$(): Observable<OrdersStatusEvent> {
    return this._orderStatusEventSubject.pipe(catchError(() => EMPTY));
  }

  // Constructor ------------------------------------------>

  constructor(
    private priceController: OrderPriceController,
    private configService: ConfigService,
    private userService: UserService,
    private http: HttpClient
  ) { }

  // Public methods --------------------------------------->

  public loadAll(options: {
    [key: string]: string | number | boolean;
  }): Observable<PaginationResult<Order>> {
    const params = this.getMergedQueryParam('', options);
    const url = this.configService.getUrl('orders.index');

    return this.http
      .get<PaginationResult<Order>>(url, {
        params,
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  /**
   * Sends request to api and gets back the generated
   * shared link object then we need to generate the link.
   */
  public generateLinkObjectFor(orderId: number): Observable<SharedLink> {
    const url = this.configService
      .getUrl('orders.generateLink')
      .replace(':id', orderId.toString());

    return this.http
      .post<SharedLink>(url, {}, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public getTalcColors(): Observable<TalcColor[]> {
    const url = this.configService.getUrl('colors.read', 2);
    return this.http.get<TalcColor[]>(url, { withCredentials: true });
  }

  public async save(options: OrderSaveOptions): Promise<Order> {
    return new Promise((resolve, reject) => {
      this.getOrderUrl(options.type).subscribe((url) => {
        this.getOrderPostHttp(url, options).subscribe((order) => {
          // this.userService.getUserShoppingCartOrders();
          // this.loadShoppingCartOrders();
          resolve(order);
          this._orderStatusEventSubject.next({
            type: 'add',
            payload: order,
          });
          this.loadShoppingCartOrders();
        }, error => reject(error));
      });
    });
  }

  /**
   * Returns one single order
   */
  public loadOneByReferralHash(hash: string): Observable<Order> {
    const url = this.configService
      .getUrl('orders.getOneByReferralHash')
      .replace(':hash', hash);
    return this.http.get<Order>(url, { withCredentials: true });
  }

  public loadNoFilter(): Observable<Order[]> {
    const url = this.configService.getUrl('user.allOrders');
    return this.http
      .get<Order[]>(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public async load(options?: OrderLoadOptions): Promise<Order[]> {
    if (options?.justShoppingCart) {
      return await this.loadShoppingCartOrders();
    }

    return [];
  }

  public async updateRaw(options: OrderUpdateOptions): Promise<Order> {
    return await firstValueFrom(
      this.http.post<Order>('orders.updateRaw', options.order)
    );
  }

  public remove(options: OrderRemoveOptions): Promise<boolean> {
    const url = this.configService.getUrl('orders.removeOne');

    return new Promise((resolve, reject) => {
      this.http
        .delete(url + options.key, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (result) => {
            const res = result === 'true' || result === true ? true : false;
            resolve(res);
          },
          (error) => {
            console.error(error);
            reject(false);
          }
        );
    });
  }

  public async lookup(key: number): Promise<boolean> {
    const url = this.getLookupLocalOrderOnServerUrl(key);
    return false;
  }

  public updateStatus(id: number, status: OrderStatuses): Observable<Order> {
    const url = this.configService.getUrl('orders.updateStatus');

    return this.http
      .put<Order>(url, { id, status }, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public announcePrice(id: number, price: number): Observable<Order> {
    const url = this.configService.getUrl('orders.announcePrice');

    return this.http
      .put<Order>(url, { id, price }, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public getEveryUserOrder(): Promise<Order[]> {
    const url = this.configService.getUrl('orders.userEveryOrders');
    return firstValueFrom(
      this.http
        .get<Order[]>(url, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
    );
  }

  public getDownloadOrders() {
    const url = this.configService.getUrl('orders.getDownloadOrders');
    return firstValueFrom(
      this.http
        .get<Order[]>(url, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
    );
  }

  public getOneOrder(id: number, protect?: boolean) {
    const url = this.configService
      .getUrl('orders.' + (protect ? 'getOneProtected' : 'getOne'))
      .replace(':id', `${id}`);

    return firstValueFrom(
      this.http
        .get<Order>(url, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
    );
  }


  public getBindSalaryFee(key: any) {
    const url = this.configService
      .getUrl('settings.bindSalaryFee', 3).replace(':key', `${key}`);

    return firstValueFrom(
      this.http
        .get<any>(url, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
    );
  }


  public calculatePrice(order: OrderDto, coverPrice?:number, bindSalaryFee?:number, binding?: StockItem, priceRangeBinding?: any): number {
    const blocksPrice =
      this.priceController.calculateBlocksPrice(order.blocks) || 0;

    const bindingPrice = priceRangeBinding ? priceRangeBinding : this.priceController.calculateBindingPrice(binding) || 0;
    const price = (blocksPrice + bindingPrice + coverPrice + bindSalaryFee) * +order.copiesNumber;

    this.priceUpdate.next(price);

    // return Math.ceil(price / 1000) * 1000;
    return price;
  }

  public generateColorTypeName(value: PrintColorsConstant, twoSided: boolean) {
    return (twoSided ? '2' : '1') + value;
  }

  /**
   * Returns an array containing
   * @param order
   * @returns {string[]}
   */
  public getOrderPageSizes(order: Order): string[] {
    return _.uniq(
      getMainFileOrError(order.files).fileBlocks?.map((item) => {
        return item.stock?.name!;
      }) || []
    );
  }

  /**
   * Before anything you should generate this.
   */
  // public getOrdersInfo(orders: Order[]): {
  //   [key: string]: (OrderInfo | undefined)[];
  // } {
  //   const orderTypes: any = [];

  //   for (let order of orders) {
  //     if (!orderTypes[order.type]) {
  //       orderTypes[order.type] = [];
  //     }

  //     orderTypes[order.type].push(order);
  //   }

  //   return orderTypes;
  // }

  public getOneOrderInfo(order: Order): OrderInfo | undefined {
    const mainFile = getMainFileOrNothing(order.files);

    if (!mainFile) {
      console.warn('Main file for order with id:', order.id, 'Not found!');
      return undefined;
    }

    const size = _.uniq(
      mainFile.fileBlocks?.map((block) => block.stock?.name!)
    ).join('-');

    const printType = (() => {
      switch (order.color) {
        case 'mixed':
          return 'ترکیبی';

        case 'bw':
          return 'سیاه و سفید';

        case 'hc':
          return 'نیمه رنگی';

        case 'c':
          return 'تمام رنگی';

        default:
          return 'هیچ';
      }
    })();

    const everyFileBlockIsTwoSide = mainFile.fileBlocks?.every((block) => {
      return block?.stock?.name.startsWith('2');
    });

    const twoSideOrOneSide = everyFileBlockIsTwoSide ? 'دو رو' : 'یک رو';
    const bindingOrCover = order?.binding?.name || 'کاور';
    const talcColor =
      order.binding?.talcColor?.name || order.talcColor?.name || undefined;
    const ranges = this.getOrderRanges(mainFile.fileBlocks!);

    return {
      totalPageNum: order.pageCount,
      copies: order.copiesNumber,
      type: printType,
      binding: bindingOrCover,
      side: twoSideOrOneSide,
      talcColor: talcColor,
      status: order.status,
      instance: order,
      ranges: ranges,
      size: size!,
    };
  }

  public getOrderRanges(printBlocks: PrintBlockItem[]): {
    [key: string]: string[];
  } {
    const ranges: { [key: string]: string[] } = {};

    printBlocks.forEach((block) => {
      const typeArray = [...block?.stock?.type!];
      const type = typeArray.shift() ? typeArray.join('') : '';
      const rangeString =
        block.start === block.end
          ? `${block.start}`
          : `${block.start} - ${block.end}`;

      if (!ranges[type]) {
        ranges[type] = [rangeString];
        return;
      }

      ranges[type].push(rangeString);
    });

    return ranges;
  }

  /**
   * Get order fileBlocks
   */
  public getOrderFileBlocks(order: Order): PrintBlockItem[] {
    return getMainFileOrNothing(order.files)?.fileBlocks || [];
  }

  /**
   * True means twoSided, false means oneSided
   * @param order
   * @returns {boolean}
   */
  public isOrderTwoSided(order: Order): boolean {
    const fileBlocks = this.getOrderFileBlocks(order);
    const everyStockCheck = fileBlocks.map((item) => {
      return item.stock?.type?.startsWith('2')!;
    });

    return _.every(everyStockCheck, (item: any) => item);
  }

  // Private methods -------------------------------------->

  private getMergedQueryParam(
    url: string,
    objectOfParams: Record<string, any>
  ): HttpParams {
    for (const key in objectOfParams) {
      if (!objectOfParams[key]) {
        delete objectOfParams[key];
      }
    }

    const params = new HttpParams({ fromObject: objectOfParams });
    return params;
  }

  private loadShoppingCartOrders(): Promise<Order[]> {
    return firstValueFrom(this.userService.getUserShoppingCartOrders());
  }

  private getOrderUrl(pointer?: string): Observable<string> {
    return new Observable((subscriber) => {
      let url: string;

      if (pointer && pointer === 'thesis-edit') {
        url = this.configService.getUrl('orders.createThesisEditOrder');
      } else {
        url = this.configService.getUrl('orders.createOrder');
      }

      subscriber.next(url);
    });
  }

  private lookupHttpObject(url: string) {
    return this.http.get<boolean>(url);
  }

  private getLookupLocalOrderOnServerUrl(key: number): string {
    return (
      this.configService.getUrl('orders.lookupLocalOrderOnServer') + `/${key}`
    );
  }

  private getOrderPostHttp(
    url: string,
    options: OrderSaveOptions
  ): Observable<Order> {
    return this.http
      .post<Order>(url, options.order, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }
}
